'use client';
import { useQueryClient } from '@tanstack/react-query';
import {
  FileRequestPlaceholderWithRelations,
  FileRequestWithRelations,
} from 'bff';
import { useCallback, useState, useMemo, useEffect, useRef } from 'react';
import {
  buildClaimPendingFileRequestQueryKey,
  DOCUMENTATION_FILES_QUERY_KEY,
  FileListPlaceholder,
  UploadFile,
  UploadFileRef,
  useApiError,
  useFileRequest,
} from 'shared-components';
import {
  Button,
  useToast,
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from 'ui';
import wretch from 'wretch';
import FormDataAddon from 'wretch/addons/formData';
import { FileTypes, UserType } from 'database';
import { useSharedClaim } from '../contexts/SharedClaimContext';
import { withEnablement } from '../hocs/withEnablement';
import { useTranslations } from 'i11n';
import { format } from 'date-fns';
import { localeDateFormat } from 'utils';

export const buildClaimDocumentationDataListQueryKey = (claimId: string) =>
  `claim-${claimId}-files`;

const UnsafeFileRequestPlaceholder = ({
  placeholder,
}: {
  placeholder: FileRequestPlaceholderWithRelations;
}) => {
  const [files, setFiles] = useState<
    {
      token: string;
      name: string;
      size: number;
    }[]
  >([]);

  const [isLoading, setIsLoading] = useState(false);

  const [isFileLoaded, setIsFileLoaded] = useState(false);

  const { confirmFileRequestPlaceholder, tokenizeFile, readOnly, queryKey } =
    useFileRequest();

  const { claim } = useSharedClaim();

  const { handleError } = useApiError();

  const { toast } = useToast();

  const queryClient = useQueryClient();

  const uploadFileRef = useRef<UploadFileRef>(null);

  const handleConfirmPlaceholder = useCallback(() => {
    setIsLoading(true);

    confirmFileRequestPlaceholder({
      params: {
        claimId: claim.id,
        fileRequestId: placeholder.file_request_id,
        placeholderId: placeholder.id,
      },
      data: {
        files,
      },
    })
      .then(() => {
        queryClient.invalidateQueries([queryKey]);
        queryClient.invalidateQueries([
          buildClaimPendingFileRequestQueryKey(claim.id),
        ]);
        queryClient.invalidateQueries([
          buildClaimDocumentationDataListQueryKey(claim.id),
        ]);
        queryClient.invalidateQueries([DOCUMENTATION_FILES_QUERY_KEY]);
      })
      .then(() => {
        uploadFileRef.current?.reset();

        toast({
          title: 'Documentación enviada',
          description: 'La documentación ha sido enviada correctamente',
        });

        setFiles([]);
      })
      .finally(() => setIsLoading(false))
      .catch(handleError);
  }, [files, setFiles]);

  // Manejador para detener la propagación de eventos
  const stopPropagation = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
  };

  return (
    <div className='flex flex-col space-y-2 h-full w-full' key={placeholder.id}>
      <div>
        <UploadFile
          disabled={readOnly}
          ref={uploadFileRef}
          fileTypes={placeholder.types as FileTypes[]}
          key={placeholder.id}
          onRemoveFile={(index) =>
            setFiles((files) => files.filter((_, i) => i !== index))
          }
          maxFiles={placeholder.max_files}
          onUploadFile={async (file) => {
            const { fields, presigned_url, token, fetch_url } =
              await tokenizeFile({
                data: {
                  name: file.name,
                  size: file.size,
                },
              });

            await wretch(presigned_url)
              .addon(FormDataAddon)
              .formData({
                ...fields,
                file,
              })
              .post()
              .res();

            setFiles((files) =>
              files.concat([
                {
                  name: file.name,
                  size: file.size,
                  token,
                },
              ]),
            );
            console.log('lg files', files);
            setIsFileLoaded(true);

            return {
              url: fetch_url,
              token,
            };
          }}
          label={placeholder.tag.name}
          className='h-[12.8rem] border-slate-300 rounded-xl'
          render={{
            submit: () => {
              if (!files.length) return;

              return (
                <div className='!mt-0' onClick={stopPropagation}>
                  <Button
                    disabled={isLoading || !isFileLoaded}
                    onClick={(e) => {
                      stopPropagation(e);
                      handleConfirmPlaceholder();
                    }}
                    loading={isLoading}
                    type='button'
                    size='xs'
                  >
                    Enviar documento
                  </Button>
                </div>
              );
            },
          }}
        >
          {placeholder.context && (
            <p className='text-sm text-muted-foreground'>
              {placeholder.context}
            </p>
          )}
        </UploadFile>
      </div>
    </div>
  );
};

const FileRequestPlaceholder = withEnablement(
  UnsafeFileRequestPlaceholder,
  'documentation',
);

export const FileRequestMessage = ({
  fileRequests,
  className,
  userType,
}: {
  fileRequests: FileRequestWithRelations[];
  className?: string;
  userType?: UserType;
}) => {
  const t = useTranslations('fileRequest');

  const dedupedPlaceholders = useMemo(() => {
    const placeholdersByTag = new Map<
      string,
      {
        fileRequest: FileRequestWithRelations;
        placeholder: FileRequestPlaceholderWithRelations;
      }
    >();

    fileRequests.forEach((fileRequest) => {
      fileRequest.placeholders.forEach((placeholder) => {
        const dedupeKey =
          placeholder.tag?.name?.trim().toLocaleLowerCase() ||
          placeholder.tag_id;
        const existing = placeholdersByTag.get(dedupeKey);
        if (!existing) {
          placeholdersByTag.set(dedupeKey, {
            fileRequest,
            placeholder,
          });
          return;
        }

        const existingIsCancelled = !!existing.placeholder.deleted_at;
        const currentIsActive = !placeholder.deleted_at;

        // Prioritize active placeholders over cancelled ones.
        if (existingIsCancelled && currentIsActive) {
          placeholdersByTag.set(dedupeKey, {
            fileRequest,
            placeholder,
          });
        }
      });
    });

    return Array.from(placeholdersByTag.values());
  }, [fileRequests]);

  return (
    <FileListPlaceholder.Container className={className}>
      {dedupedPlaceholders.map(({ fileRequest, placeholder }) => {
        if (placeholder.deleted_at && userType === 'insurer') {
          return (
            <div
              key={`${fileRequest.id}-${placeholder.id}`}
              className='cursor-default flex items-center justify-center rounded-xl text-base_brand border border-primary py-1 px-2 h-6 gap-1 opacity-50'
            >
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className='text-base_brand text-foreground font-medium'>
                      {placeholder.tag.name} ({t('cancelled')})
                    </span>
                  </TooltipTrigger>
                  <TooltipPortal>
                    <TooltipContent className='text-xs'>
                      Motivo: {placeholder.deleted_reason}
                      <br />
                      <span className='text-xs text-muted-foreground'>
                        Cancelado el{' '}
                        {localeDateFormat(new Date(placeholder.deleted_at))}
                      </span>
                    </TooltipContent>
                  </TooltipPortal>
                </Tooltip>
              </TooltipProvider>
            </div>
          );
        }

        if (placeholder.dirty) {
          return (
            <FileListPlaceholder
              key={`${fileRequest.id}-${placeholder.id}`}
              placeholder={placeholder}
            />
          );
        }

        return (
          <FileRequestPlaceholder
            key={`${fileRequest.id}-${placeholder.id}`}
            placeholder={placeholder}
          />
        );
      })}
    </FileListPlaceholder.Container>
  );
};
